import React, { useState, useRef, useEffect } from 'react';
import './About.css';

const categoryContent = {
  'INDUSTRIAL AUTOMATION': {
    title: 'Industrial automation solutions for modern operations',
    description:
      'We design intelligent automation systems that optimize production lines, reduce downtime, and improve operational visibility across your industrial workflows. From connected sensors and PLC integrations to real-time dashboards and predictive maintenance, our solutions help teams detect issues early, automate repetitive decisions, and scale operations with confidence. We align technology with your on-ground processes to deliver safer plants, stronger throughput, and measurable performance gains.',
    video: 'https://websitestorage.blr1.cdn.digitaloceanspaces.com/plc.mp4',
  },
  WEBSITES: {
    title: 'Building digital experiences that engage and convert',
    description:
      'We craft high-performing websites that are fast, fluid, and impossible to ignore. Every pixel and interaction is engineered to convert curiosity into loyalty.',
    image: '/images/main-slider/website.jpg',
  },
  APPS: {
    title: 'Mobile apps built to scale and delight.',
    description:
      'From consumer apps to enterprise tools, our product teams blend UX rigor with robust engineering so your app feels as good as it functions.',
    image: '/images/main-slider/mobile_pic.jpg',
  },
};

const About = () => {
  const categories = Object.keys(categoryContent);
  const [activeCategory, setActiveCategory] = useState(categories[0]);
  const activeData = categoryContent[activeCategory];
  const videoRef = useRef(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const playVideoSafely = () => {
    if (!videoRef.current) return;
    const playPromise = videoRef.current.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        // Ignore autoplay interruption errors from browser timing/race conditions.
      });
    }
  };

  const handleVideoMouseEnter = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const handleVideoMouseLeave = () => {
    playVideoSafely();
  };

  // Reset video when category changes
  useEffect(() => {
    if (videoRef.current && activeData.video) {
      videoRef.current.currentTime = 0;
      playVideoSafely();
    }
  }, [activeCategory, activeData.video]);

  // Reset image index when category changes
  useEffect(() => {
    if (activeCategory !== 'MARKETING') {
      setCurrentImageIndex(0);
    }
  }, [activeCategory]);

  // Auto-scroll marketing images
  useEffect(() => {
    if (activeCategory === 'MARKETING' && activeData.images) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % activeData.images.length);
      }, 4000); // Change image every 3 seconds

      return () => clearInterval(interval);
    }
  }, [activeCategory, activeData.images]);

  const nextImage = () => {
    if (activeData.images) {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % activeData.images.length);
    }
  };

  const prevImage = () => {
    if (activeData.images) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === 0 ? activeData.images.length - 1 : prevIndex - 1
      );
    }
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  return (
    <section className="about" id="about">
      <div className="about-container">
        <div className="about-sidebar">
          {categories.map((category, index) => (
            <div
              key={index}
              className={`sidebar-category ${activeCategory === category ? 'active' : ''}`}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </div>
          ))}
        </div>

        <div className="about-main">
          {activeCategory !== 'MARKETING' && (
            <div className="about-content-left">
              <div className="about-header">
                <div className="about-logo">
                  <span className="about-company-name">Fractallogy</span>
                </div>
                <div className="yellow-separator"></div>
              </div>

              <h2 className="about-title">{activeData.title}</h2>

              <p className="about-description">{activeData.description}</p>

              <div className="about-cta">
                <a href="#contact" className="cta-button-yellow">
                  Let's Collaborate
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
            </div>
          )}

          <div className={`about-content-right ${activeCategory === 'MARKETING' ? 'marketing-full-width' : ''}`}>
            <div className={`about-image-wrapper ${activeCategory === 'MARKETING' ? 'marketing-images' : ''}`}>
              <div className="image-gradient"></div>
              {activeData.video ? (
                <video
                  ref={videoRef}
                  src={activeData.video}
                  poster="/images/menu_icons/industry.jpg"
                  className="about-feature-image"
                  autoPlay
                  loop
                  muted
                  playsInline
                  onMouseEnter={handleVideoMouseEnter}
                  onMouseLeave={handleVideoMouseLeave}
                />
              ) : activeData.images ? (
                <div className="marketing-image-gallery">
                  <div 
                    className="marketing-slider-container"
                    style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
                  >
                    {activeData.images.map((img, index) => (
                      <img
                        key={index}
                        src={img}
                        alt={`${activeCategory} case study ${index + 1}`}
                        className="about-feature-image marketing-gallery-image"
                      />
                    ))}
                  </div>
                  <button 
                    className="marketing-slider-button marketing-slider-prev"
                    onClick={prevImage}
                    aria-label="Previous image"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M15 18l-6-6 6-6"/>
                    </svg>
                  </button>
                  <button 
                    className="marketing-slider-button marketing-slider-next"
                    onClick={nextImage}
                    aria-label="Next image"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 18l6-6-6-6"/>
                    </svg>
                  </button>
                  <div className="marketing-slider-dots">
                    {activeData.images.map((_, index) => (
                      <button
                        key={index}
                        className={`marketing-dot ${index === currentImageIndex ? 'active' : ''}`}
                        onClick={() => goToImage(index)}
                        aria-label={`Go to image ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <img
                  src={activeData.image}
                  alt={`${activeCategory} case study`}
                  className="about-feature-image"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
